import React, { useEffect, useState } from "react";
import { Loader2, RotateCw } from "lucide-react";
import { reApi } from "@/lib/residentApi";
import { OpenResidentButton } from "@/components/nds/OpenResidentButton";
import { ListTools, sortRows } from "@/components/nds/ListTools";

const GET = { name: (r) => r.name, date: (r) => r.last_checkin?.checkin_date };

const TILES = [
  ["never", "Never checked in", "border-red-400/50 bg-red-400/10 text-red-200"],
  ["due", "Check-in due (incl. never)", "border-amber-400/50 bg-amber-400/10 text-amber-200"],
  ["answers_due", "Questions not answered", "border-sky-400/50 bg-sky-400/10 text-sky-200"],
  ["draft", "Drafts", "border-white/20 bg-white/[0.03] text-nex-silver"],
  ["awaiting_review", "Awaiting review", "border-yellow-400/50 bg-yellow-400/10 text-yellow-200"],
  ["too_soon", "Too soon to count", "border-orange-400/50 bg-orange-400/10 text-orange-200"],
  ["payouts", "Payouts to approve", "border-violet-400/50 bg-violet-400/10 text-violet-200"],
];
const tone = (s) => (s == null ? "text-nex-muted" : s >= 4 ? "text-emerald-300" : s === 3 ? "text-lime-200" : s === 2 ? "text-amber-300" : "text-red-300");
const scoreTone = (s) => (s == null ? "border-white/10 text-nex-muted" : s >= 70 ? "border-emerald-400/50 text-emerald-300" : s >= 45 ? "border-amber-400/50 text-amber-300" : "border-red-400/50 text-red-300");
const arrow = (a, b) => (b == null || a == null ? "" : a > b ? "↑" : a < b ? "↓" : "→");

function GroupCell({ items, qs }) {
  if (!items.length) return <span className="font-mono text-[10px] text-nex-muted">—</span>;
  return (
    <div className="space-y-1">{items.map((a) => (
      <div key={a.no} title={`${qs[a.no].prompt}\n→ ${a.text}`} className="flex items-center gap-1.5 text-[11px]">
        <span className="text-nex-muted w-16 truncate">{qs[a.no].short}</span>
        <b className={`font-mono ${tone(a.score)}`}>{a.score}</b><span className="text-nex-muted text-[10px]">{arrow(a.score, a.prev)}</span>
      </div>))}
    </div>
  );
}

/** Pillar-board style view of weekly check-ins: who's due, what residents said, their score, and a Check in action. */
export default function CheckinBoard({ onCheckIn }) {
  const [d, setD] = useState(null);
  const [filter, setFilter] = useState(null);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("urgent");
  const load = () => reApi.get("/checkin-board").then(setD).catch(() => setD({ error: true }));
  useEffect(() => { load(); }, []);
  if (!d) return <div className="p-6 flex items-center gap-2 text-nex-muted"><Loader2 className="h-4 w-4 animate-spin" /> Loading board…</div>;
  if (d.error) return <p data-testid="ck-board-error" className="p-6 text-red-300">Couldn't load the check-in board.</p>;
  const qs = Object.fromEntries(d.questions.map((x) => [x.no, x]));
  const rows = sortRows(d.rows.filter((r) => !filter || filter === "payouts" || (filter === "draft" ? r.draft : r[filter])), GET, sort, q);
  return (
    <div data-testid="ck-board" className="p-5 space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-7 gap-2">
        {TILES.map(([k, label, cls]) => (
          <button key={k} type="button" disabled={k === "payouts"} title={k === "payouts" ? "Approve these in Case Session Payouts (Finance Engine)" : undefined} onClick={() => setFilter(filter === k ? null : k)} data-testid={`ck-tile-${k}`} aria-pressed={filter === k}
            className={`text-left px-3 py-2.5 border rounded-sm transition-opacity ${cls} ${filter && filter !== k ? "opacity-40" : ""}`}>
            <div className="font-mono text-[9px] tracking-[0.18em] uppercase">{label}</div><div className="text-2xl font-semibold mt-1">{d.tiles[k]}</div>
          </button>
        ))}
      </div>
      <div className="flex items-center gap-3">
        <ListTools prefix="ck-board" q={q} setQ={setQ} sort={sort} setSort={setSort} get={GET} />
        {filter && <button type="button" onClick={() => setFilter(null)} data-testid="ck-board-clear" className="font-mono text-[10px] tracking-[0.18em] uppercase text-nex-gold">Clear filter</button>}
        <span data-testid="ck-board-count" className="font-mono text-[10px] text-nex-muted">{rows.length} of {d.rows.length} residents</span>
        <button type="button" onClick={load} data-testid="ck-board-refresh" aria-label="Refresh" className="ml-auto p-1.5 text-nex-muted hover:text-nex-gold"><RotateCw className="h-3.5 w-3.5" /></button>
      </div>
      <div className="overflow-x-auto border border-white/10 rounded-sm">
        <table data-testid="ck-board-table" className="w-full min-w-[1100px] text-left">
          <thead><tr className="font-mono text-[9px] tracking-[0.16em] uppercase text-nex-muted">
            <th className="px-3 py-2 font-normal">Resident</th>
            {d.groups.map(([k, l]) => <th key={k} className="px-2 py-2 font-normal">{l}</th>)}
            <th className="px-2 py-2 font-normal">Score</th><th className="px-3 py-2 font-normal">Last check-in</th>
          </tr></thead>
          <tbody>{rows.map((r) => (
            <tr key={r.resident_id} data-testid={`ck-row-${r.resident_id}`} className="border-t border-white/5 align-top">
              <td className="px-3 py-2">
                <div className="text-sm text-nex-silver flex flex-wrap items-center gap-2">{r.name}<OpenResidentButton residentId={r.resident_id} name={r.name} /></div>
                <div className="font-mono text-[10px] text-nex-muted">{r.program_day ? `Day ${r.program_day}` : ""}{r.answered_at ? ` · answered ${r.answered_at.slice(0, 10)}` : " · no answers yet"}</div>
              </td>
              {d.groups.map(([k]) => <td key={k} className="px-2 py-2"><GroupCell items={r.answers.filter((a) => a.group === k)} qs={qs} /></td>)}
              <td className="px-2 py-2"><span data-testid={`ck-score-${r.resident_id}`} className={`inline-block px-2 py-1 border rounded-sm font-mono text-sm ${scoreTone(r.score)}`}>{r.score ?? "—"}<span className="text-[10px] ml-1">{arrow(r.score, r.prev_score)}</span></span></td>
              <td className="px-3 py-2 whitespace-nowrap">
                <div data-testid={`ck-last-${r.resident_id}`} className={`text-[11px] ${r.due ? "text-amber-300" : "text-nex-muted"}`}>
                  {r.last_checkin ? `${r.last_checkin.checkin_date} · ${r.last_checkin.completed_by?.name || "—"} · ${r.last_checkin.status}` : "Never checked in"}
                </div>
                {r.last_checkin?.counted === false && <div className="text-[10px] text-orange-300">Not counted</div>}
                <button type="button" onClick={() => onCheckIn(r)} data-testid={`ck-checkin-${r.resident_id}`}
                  className={`mt-1 px-2 py-1 border rounded-sm font-mono text-[9px] tracking-[0.16em] uppercase transition-colors ${r.due ? "border-nex-gold/60 text-nex-gold hover:bg-nex-gold/10" : "border-white/15 text-nex-muted hover:text-nex-silver"}`}>
                  {r.draft ? "Open draft" : "Check in"}
                </button>
              </td>
            </tr>
          ))}</tbody>
        </table>
        {!rows.length && <p data-testid="ck-board-empty" className="p-6 text-center text-sm text-nex-muted">Nobody matches this filter.</p>}
      </div>
    </div>
  );
}
